import { FC, PropsWithChildren, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { IconArrowRight } from "@/components/_icons/IconArrowRight";
import { IconCircleGroup } from "@/components/_icons/IconCircleGroup";
import { IconCircleSolid } from "@/components/_icons/IconCircleSolid";
import { IconPen } from "@/components/_icons/IconPen";
import { IconTrash } from "@/components/_icons/IconTrash";
import { DeleteConfirmation } from "@/components/_molecules/delete-confirmation";
import { useGetLabel } from "@/domain/label/hooks/api/use-get-label";
import { useGetLabelGroupListForWorkspace } from "@/domain/label/hooks/api/use-get-label-group-list-for-workspace";

import { AddLabelToGroupAction } from "./actions/add-label-to-group-action";
import { DeleteLabelAction } from "./actions/delete-label-action";
import { RemoveLabelFromGroupAction } from "./actions/remove-label-from-group-action";

export interface LabelActionButtonProps extends PropsWithChildren {
  label: {
    id: string;
  };
  workspace: {
    id: string;
  };
  handleEditLabel: () => void;
  asChild?: boolean;
}

export const LabelActionButton: FC<LabelActionButtonProps> = ({
  workspace,
  handleEditLabel,
  asChild,
  children,
  ...props
}) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [deleteConfirmationisOpen, setDeleteConfirmationisOpen] = useState(false);
  const [triggerDelete, setTriggerDelete] = useState(false);

  const { label } = useGetLabel({ id: props.label.id });
  const { labelGroupList } = useGetLabelGroupListForWorkspace({ workspaceId: workspace.id });

  const filteredLabelGroupList = useMemo(() => {
    return labelGroupList
      .slice()
      .filter((group) => group.id !== label?.labelGroupId)
      .sort((a, b) => {
        if (!a || !b) {
          return 0;
        }
        return a.title.localeCompare(b.title);
      });
  }, [labelGroupList, label?.labelGroupId]);

  if (!label) {
    return <></>;
  }

  const hasGroupOption = Boolean(label.labelGroupId) || filteredLabelGroupList.length > 0;

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild={asChild}>{children}</DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="cursor-pointer text-foreground/60 hover:bg-accent hover:text-accent-foreground"
            aria-selected
            onClick={handleEditLabel}
          >
            <div className="relative flex items-center overflow-hidden text-ellipsis">
              <IconPen className="mr-2 h-3 w-3" />
              {t("label.editLabel")}
            </div>
          </DropdownMenuItem>

          {hasGroupOption && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer text-foreground/60 hover:bg-accent hover:text-accent-foreground">
                <div className="relative flex items-center overflow-hidden text-ellipsis">
                  <IconArrowRight className="mr-2 h-3 w-3" />
                  {label.labelGroupId ? t("label.moveToGroup") : t("label.addToGroup")}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {label.labelGroupId && (
                    <RemoveLabelFromGroupAction label={label} workspace={workspace} asChild>
                      <DropdownMenuItem className="flex cursor-pointer flex-row items-center text-foreground/60 hover:bg-accent hover:text-accent-foreground">
                        <IconCircleSolid className="mr-2 h-2.5 w-2.5 text-muted-foreground" />
                        {t("label.noGroup")}
                      </DropdownMenuItem>
                    </RemoveLabelFromGroupAction>
                  )}
                  {filteredLabelGroupList.map((group) => (
                    <AddLabelToGroupAction key={group.id} label={label} labelGroup={group} workspace={workspace} asChild>
                      <DropdownMenuItem className="flex cursor-pointer flex-row items-center text-foreground/60 hover:bg-accent hover:text-accent-foreground">
                        <IconCircleGroup className="mr-1.5 h-3 w-3" style={{ color: group.color }} />
                        {group.title}
                      </DropdownMenuItem>
                    </AddLabelToGroupAction>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          )}
          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="cursor-pointer text-foreground/60 hover:bg-accent hover:text-accent-foreground"
            aria-selected
            onClick={() => {
              setDeleteConfirmationisOpen(true);
            }}
          >
            <div className="relative flex items-center overflow-hidden text-ellipsis">
              <IconTrash className="mr-2 h-3 w-3" />
              {t("label.deleteLabel")}
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteLabelAction asChild label={label} workspace={workspace} forceDelete={triggerDelete}>
        <DeleteConfirmation
          open={deleteConfirmationisOpen}
          onOpenChange={setDeleteConfirmationisOpen}
          onConfirm={() => {
            setTriggerDelete(true);
          }}
          title={t("label.deleteConfirmTitle")}
          description={t("label.deleteConfirmDescription")}
        />
      </DeleteLabelAction>
    </>
  );
};
